import { Status } from '@/common/enums/Status';
import { ReservationOfferTransport, TableColumn } from '@/new-transport/_models/transport-list.model';
import { NgStyle } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	QueryList,
	viewChild,
} from '@angular/core';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StatusLabelPipe } from '../../../common/pipes/status-label.pipe';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { MailGenerateDialogComponent } from '../mail-generate-dialog/mail-generate-dialog.component';

@Component({
	selector: 'app-transport-reservation-list-table',
	standalone: true,
	imports: [
		MatTableModule,
		NgStyle,
		RouterModule,
		MatProgressSpinnerModule,
		MatSortModule,
		StatusLabelPipe,
		MatCheckboxModule,
		MatButtonModule,
	],
	providers: [StatusLabelPipe],
	templateUrl: './transport-reservation-list-table.component.html',
	styleUrl: './transport-reservation-list-table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportReservationListTableComponent implements AfterViewInit, OnDestroy {
	readonly columns = input.required<TableColumn[]>();
	readonly data = input.required<ReservationOfferTransport[]>();

	readonly refreshView = output<void>();

	readonly selection = new SelectionModel<ReservationOfferTransport>(true, []);

	readonly dataSource = computed(() => {
		const table = new MatTableDataSource<ReservationOfferTransport>(this.data());
		this.setSorting(table);
		return table;
	});

	readonly tableWidth = computed(() => {
		const totalWidth = this.columns().reduce((sum, column) => sum + parseInt(column.width), 0);
		const tableElement = document.querySelector('app-transport-reservation-list-table') as HTMLElement;
		if (tableElement) {
			const { offsetWidth } = tableElement;
			if (offsetWidth > totalWidth) {
				return '100%';
			}
			return totalWidth + 'px';
		} else {
			return '0px';
		}
	});

	readonly displayedColumns = computed(() => this.columns().map((item) => item.field));

	readonly isLoadingResults = computed(() => !this.data() || this.data().length === 0);

	private observer: MutationObserver;

	private readonly sort = viewChild(MatSort);

	private readonly el = inject(ElementRef);
	private readonly statusLabelPipe = inject(StatusLabelPipe);
	private readonly matDialog = inject(MatDialog);

	ngAfterViewInit(): void {
		this.observeMatTable();
		this.setSorting(this.dataSource());
	}

	ngOnDestroy(): void {
		this.observer.disconnect();
	}

	isAllSelected(): boolean {
		const numSelected = this.selection.selected.length;
		const numRows = this.dataSource().data.length;
		return numSelected === numRows;
	}

	toggleAllRows(): void {
		if (this.isAllSelected()) {
			this.selection.clear();
			return;
		}

		this.selection.select(...this.dataSource().data);
	}

	mailDialogOpen(): void {
		this.matDialog
			.open<MailGenerateDialogComponent, number[]>(MailGenerateDialogComponent, {
				data: this.selection.selected.map((offer) => offer.reservation.id),
				width: '50%',
			})
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => {
				this.selection.clear();
				this.refreshView.emit();
			});
	}

	private observeMatTable(): void {
		this.observer = new MutationObserver(() => {
			this.columns().forEach((column) => {
				if (column.width) {
					this.setColumnStyle(column.field, column.width);
				}
			});
		});

		const nodes = document.querySelectorAll('.mat-mdc-tab-body-content');

		nodes.forEach((node) => {
			this.observer.observe(node, {
				attributes: true,
				childList: true,
				characterData: true,
				subtree: true,
			});
		});
	}

	private setColumnStyle(columnName: string, width: string): void {
		const matColumns: QueryList<HTMLElement> = this.el.nativeElement.querySelectorAll(`.mat-column-${columnName}`);

		if (matColumns) {
			matColumns.forEach((column) => {
				column.style.width = width;
				column.style.flex = `0 0 ${width}`;
			});
		}
	}

	private setSorting(dataSource: MatTableDataSource<ReservationOfferTransport>): void {
		dataSource.sort = this.sort();
		dataSource.sortingDataAccessor = (element, property) => {
			switch (property) {
				case 'bc':
					return element.reservation.id;
				case 'firstName':
					return element.reservation.ownerUser.firstName;
				case 'lastName':
					return element.reservation.ownerUser.lastName;
				case 'status':
					return this.statusLabelPipe.transform(element.reservation.status as Status);
				case 'slotCount':
					return element.slot.length;
				case 'email':
					return element.reservation.ownerUser?.userContacts[0]?.email ?? '';
				case 'phone':
					return element.reservation.ownerUser?.userContacts[0]?.phone;
				case 'product':
					return element.product.name;
				case 'board':
					return element.board.name;
				case 'insurer':
					return element.insurerName;
				default:
					return '';
			}
		};
	}
}
